/**
 * C.3d — ZIP upload import (universal fallback path).
 *
 * If the ZIP contains a single top-level directory (the common case for
 * `.obsidian-vault.zip` archives), that directory is treated as the
 * vault root so all paths align with the SharePoint layout.
 *
 * 2026-05-28 — migrated from JSZip (~95kb gz) to fflate (~3kb gz read-
 * only). JSZip's `ZipObject.async()` is a prototype method calling
 * `this.internalStream(...)`; any code that destructured the method
 * lost `this` and threw "this.internalStream is not a function" at
 * call time. fflate returns a plain `{path: Uint8Array}` record so
 * there is no method to detach.
 */

import { strFromU8, unzip, type Unzipped } from 'fflate';
import { isTextExt } from './walk';
import type { ImportFile, ImportSource } from './types';

function unzipAsync(buf: Uint8Array): Promise<Unzipped> {
  return new Promise((resolve, reject) => {
    unzip(buf, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

/**
 * Build an ImportSource by reading a .zip File. We read the file's
 * bytes ourselves (rather than passing the File) so the call works
 * uniformly in browser, jsdom/happy-dom, and Node test environments.
 */
export async function buildZipSource(file: File): Promise<ImportSource> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const entries = await unzipAsync(buf);

  // fflate already filters out directory entries — only file leaves
  // appear in the record. Detect single-top-folder by examining the
  // first segment of every path.
  const paths = Object.keys(entries);
  const topSegments = new Set<string>();
  for (const p of paths) {
    const seg = p.split('/')[0]!;
    if (seg) topSegments.add(seg);
  }
  const stripTop = topSegments.size === 1;
  const topPrefix = stripTop ? `${[...topSegments][0]!}/` : '';
  const stripCount = stripTop ? topPrefix.length : 0;

  async function* iter(): AsyncIterableIterator<ImportFile> {
    for (const original of paths) {
      let rel = original;
      if (stripTop && rel.startsWith(topPrefix)) rel = rel.slice(stripCount);
      if (!rel) continue;
      const baseSlash = rel.lastIndexOf('/');
      const basename = baseSlash >= 0 ? rel.slice(baseSlash + 1) : rel;
      const dot = basename.lastIndexOf('.');
      const ext = dot > 0 ? basename.slice(dot + 1).toLowerCase() : '';
      const isText = isTextExt(ext);
      const bytes = entries[original]!;
      const out: ImportFile = {
        path: rel,
        ext,
        sizeBytes: bytes.byteLength,
      };
      if (isText) {
        out.text = strFromU8(bytes);
        out.sizeBytes = out.text.length;
      } else {
        out.bytes = bytes;
      }
      yield out;
    }
  }

  const label = stripTop ? `ZIP: ${[...topSegments][0]!}` : `ZIP: ${file.name}`;
  return { kind: 'zip', label, files: iter };
}
